import React from 'react';
import { AmazonOutlined, ApiOutlined, GoogleOutlined } from '@ant-design/icons';
import { DetailItem, ProjectCostItem, Provider, ServiceItem } from './types';

export const providerNames: Record<Provider, string> = {
  azure: 'Azure FinOps',
  aws: 'AWS FinOps',
  gcp: 'GCP FinOps'
};

export const providerColors: Record<Provider, string> = {
  azure: 'linear-gradient(135deg, #0078d4 0%, #005a9e 100%)',
  aws: 'linear-gradient(135deg, #ff9900 0%, #ec7211 100%)',
  gcp: 'linear-gradient(135deg, #4285f4 0%, #34a853 100%)'
};

export const providerIcons: Record<Provider, React.ReactNode> = {
  azure: <ApiOutlined />,
  aws: <AmazonOutlined />,
  gcp: <GoogleOutlined />
};

export const resolveCustomerLabel = (data: any, fallbackScope?: string) =>
  data?.customerName ||
  data?.organizationName ||
  data?.tenantName ||
  data?.account?.alias ||
  data?.account?.name ||
  fallbackScope ||
  'Cliente conectado';

const currencyByProvider: Record<Provider, { locale: string; currency: string }> = {
  azure: { locale: 'pt-BR', currency: 'BRL' },
  aws: { locale: 'en-US', currency: 'USD' },
  gcp: { locale: 'pt-BR', currency: 'BRL' }
};

export const numberFormatter = (value: number, provider: Provider) =>
  new Intl.NumberFormat(currencyByProvider[provider].locale, {
    style: 'currency',
    currency: currencyByProvider[provider].currency,
    minimumFractionDigits: 2
  }).format(value || 0);

export const compactNumber = (value: number) =>
  new Intl.NumberFormat('pt-BR', { notation: 'compact', maximumFractionDigits: 1 }).format(value || 0);

const monthYearFormatter = new Intl.DateTimeFormat('pt-BR', {
  month: 'short',
  year: 'numeric'
});

const normalizeMonthYear = (value: Date) =>
  monthYearFormatter
    .format(value)
    .replace('.', '')
    .replace(/\b\w/g, letter => letter.toUpperCase());

export const formatExecutivePeriod = (data: any) => {
  const start = data?.periodRange?.start;
  const end = data?.periodRange?.end;

  if (start && end) {
    const startDate = new Date(start);
    const endDate = new Date(end);

    if (!Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime())) {
      const startLabel = normalizeMonthYear(startDate);
      const endLabel = normalizeMonthYear(endDate);
      return startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;
    }
  }

  const explicitPeriod = data?.period;
  if (typeof explicitPeriod === 'string' && explicitPeriod.trim()) {
    return explicitPeriod.trim();
  }

  const months = Array.isArray(data?.temporalCosts?.months) ? data.temporalCosts.months.filter(Boolean) : [];
  if (months.length === 1) {
    return months[0];
  }
  if (months.length > 1) {
    return `${months[0]} - ${months[months.length - 1]}`;
  }

  return 'Período atual';
};

export const titleize = (value: string) =>
  value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, letter => letter.toUpperCase());

export const formatCloudServiceName = (value: string = '') => {
  const normalized = String(value).toLowerCase().replace(/[^a-z0-9]/g, '');
  const knownServices: Record<string, string> = {
    amazonrelationaldatabaseservice: 'Amazon RDS',
    amazonelasticcomputecloudcompute: 'Amazon EC2',
    amazonsimplestorageservice: 'Amazon S3',
    amazonvirtualprivatecloud: 'Amazon VPC',
    ec2other: 'EC2 Other',
    awskms: 'AWS KMS',
    amazoncloudwatch: 'Amazon CloudWatch',
    awslambda: 'AWS Lambda',
    amazonroute53: 'Amazon Route 53'
  };

  if (knownServices[normalized]) {
    return knownServices[normalized];
  }

  return titleize(
    String(value || 'Serviço')
      .replace(/amazon/gi, 'Amazon ')
      .replace(/aws/gi, 'AWS ')
      .replace(/ec2/gi, 'EC2')
      .replace(/s3/gi, 'S3')
      .replace(/rds/gi, 'RDS')
  );
};

const shouldDropPartialCurrentMonth = (months: string[], datasets: Array<{ data: number[] }>) => {
  if (months.length < 3) return false;

  const now = new Date();
  if (now.getDate() > 7) return false;

  const lastIndex = months.length - 1;
  const lastTotal = datasets.reduce((sum, dataset) => sum + Number(dataset.data[lastIndex] || 0), 0);
  const previousTotal = datasets.reduce((sum, dataset) => sum + Number(dataset.data[lastIndex - 1] || 0), 0);

  return previousTotal > 0 && lastTotal > 0 && lastTotal < previousTotal * 0.35;
};

export const toArray = (value: unknown): any[] => (Array.isArray(value) ? value : []);

export const normalizeStatusTone = (value?: string) => {
  const normalized = String(value || '').toLowerCase();
  if (normalized.includes('critical') || normalized.includes('high') || normalized.includes('alta') || normalized.includes('remove') || normalized.includes('encerrar')) {
    return 'danger' as const;
  }
  if (normalized.includes('warn') || normalized.includes('média') || normalized.includes('media') || normalized.includes('medium') || normalized.includes('avaliar')) {
    return 'warning' as const;
  }
  if (normalized.includes('low') || normalized.includes('baixa') || normalized.includes('manter') || normalized.includes('ok') || normalized.includes('success')) {
    return 'success' as const;
  }
  return 'info' as const;
};

export const formatDataSource = (source?: string) => {
  switch (source) {
    case 'aws-cost-explorer':
      return 'Cost Explorer';
    case 'aws-service-apis':
      return 'AWS APIs';
    case 'aws-cloudwatch':
      return 'CloudWatch';
    case 'aws-ec2-api':
      return 'EC2 API';
    case 'aws-rds-api':
      return 'RDS API';
    case 'aws-s3-api':
      return 'S3 API';
    case 'aws-api-and-cloudwatch':
      return 'AWS APIs + CloudWatch';
    case 'aws-eks-api':
      return 'EKS API';
    case 'estimated':
      return 'Estimado';
    case 'not_available':
      return 'Indisponível';
    default:
      return source ? titleize(source) : '';
  }
};

export const dataSourceTone = (source?: string) => {
  if (!source) return 'info' as const;
  if (source === 'estimated') return 'warning' as const;
  if (source === 'not_available') return 'danger' as const;
  return 'success' as const;
};

export const buildSecurityRiskItems = (data: any): DetailItem[] =>
  toArray(data?.securityRiskAnalysis?.topRisks).map((risk, index) => ({
    name: risk.title || `Risco de segurança ${index + 1}`,
    type: risk.resourceType || 'Cloud Security',
    badge: risk.score ? `${Math.round(risk.score)}/100` : risk.priority || 'Priorizar',
    badgeTone: normalizeStatusTone(risk.priority || risk.severity),
    description: [
      risk.businessImpact,
      risk.technicalCause ? `Causa: ${risk.technicalCause}` : undefined,
      risk.recommendedAction ? `Ação: ${risk.recommendedAction}` : undefined
    ].filter(Boolean).join(' '),
    meta: [
      risk.priority ? `Prioridade ${risk.priority}` : undefined,
      risk.confidence ? `Confiança ${Math.round(risk.confidence)}%` : undefined,
      risk.affectedCount ? `${risk.affectedCount} recurso(s) afetado(s)` : undefined
    ].filter(Boolean).join(' • '),
    affectedResources: toArray(risk.affectedResources).map((resource: any) => ({
      name: resource.name || 'Recurso afetado',
      type: resource.type,
      issue: resource.issue,
      recommendation: resource.recommendation,
      location: resource.location
    })),
    sourceLabel: data?.securityRiskAnalysis?.model || 'Cloud Security Risk Analyzer',
    sourceTone: normalizeStatusTone(data?.securityRiskAnalysis?.posture)
  }));

export const buildSecurityRiskSubtitle = (data: any) => {
  const analysis = data?.securityRiskAnalysis;
  if (!analysis) {
    return 'Score, postura e ações de segurança priorizadas pela IA.';
  }

  const score = typeof analysis.riskScore === 'number' ? `Score ${Math.round(analysis.riskScore)}/100` : undefined;
  return [
    analysis.posture ? `Postura ${analysis.posture}` : undefined,
    score,
    analysis.executiveSummary
  ].filter(Boolean).join(' • ');
};

const buildServicesFromTemporalCosts = (data: any) => {
  const temporalCosts = data?.temporalCosts || {};
  const months = Array.isArray(temporalCosts.months) ? temporalCosts.months : [];
  const serviceKeys = Object.keys(temporalCosts).filter(key => key !== 'months');
  const totals = serviceKeys
    .map(key => {
      const values = Array.isArray(temporalCosts[key]) ? temporalCosts[key] : [];
      const totalCost = values.reduce((sum: number, value: unknown) => sum + Number(value || 0), 0);

      return {
      name: formatCloudServiceName(key),
        totalCost,
        cost: totalCost,
        percentage: 0
      };
    })
    .filter(service => service.totalCost > 0);
  const grandTotal = totals.reduce((sum, service) => sum + service.totalCost, 0);

  return totals.map(service => ({
    ...service,
    percentage: grandTotal > 0 ? (service.totalCost / grandTotal) * 100 : 0,
    averageMonthlyCost: service.totalCost / Math.max(months.length || 1, 1)
  }));
};

// Matches a display service name back to its raw key in temporalCosts
// (topServices/temporalCosts keys share the same raw service string
// server-side - see gcpRealDataService.ts - but the frontend only keeps
// the formatted display name past buildServices, so this fuzzy match is
// what pickTemporalSeries and the per-card monthlyBreakdown both rely on).
const findTemporalServiceKey = (temporalCosts: Record<string, any>, serviceName: string) => {
  const keys = Object.keys(temporalCosts || {}).filter(key => key !== 'months');
  const normalizedServiceName = serviceName.toLowerCase();
  return (
    keys.find(key => key.toLowerCase() === normalizedServiceName.replace(/[^a-z0-9]/g, '')) ||
    keys.find(key => titleize(key).toLowerCase() === normalizedServiceName) ||
    keys.find(key => normalizedServiceName.includes(key.toLowerCase()) || key.toLowerCase().includes(normalizedServiceName.replace(/\s+/g, ''))) ||
    undefined
  );
};

const buildMonthlyBreakdown = (data: any, serviceName: string): ServiceItem['monthlyBreakdown'] => {
  const temporalCosts = data?.temporalCosts || {};
  const months: string[] = Array.isArray(temporalCosts.months) ? temporalCosts.months : [];
  const matchKey = findTemporalServiceKey(temporalCosts, serviceName);
  const values = matchKey && Array.isArray(temporalCosts[matchKey]) ? temporalCosts[matchKey] : [];
  if (!months.length || !values.length) return undefined;

  return months.map((month, index) => ({ month, value: Number(values[index] || 0) }));
};

// Index of the last CLOSED month in temporalCosts.months - mirrors the
// backend's own monthlyAverageCalculation (completedMonths/excludedPartialMonth),
// the same source of truth already used for "Média Mensal" elsewhere, instead
// of the ad-hoc heuristic in shouldDropPartialCurrentMonth. A still-open
// current month is always the last entry when excluded.
const lastClosedMonthIndex = (monthsLength: number, calculation: any): number => {
  const hasOpenMonth = Boolean(calculation?.excludedPartialMonth);
  const index = hasOpenMonth ? monthsLength - 2 : monthsLength - 1;
  return index >= 0 ? index : -1;
};

// monthlyBreakdown trimmed to drop a trailing still-open current month, if
// any - the last entry here is always a real, closed "atual", and slicing
// the last N entries always gives N closed months regardless of whether
// the raw series happened to include an open month or not.
const buildClosedMonthlyBreakdown = (
  monthlyBreakdown: ServiceItem['monthlyBreakdown'],
  calculation: any
): ServiceItem['monthlyBreakdown'] => {
  if (!monthlyBreakdown?.length) return undefined;
  const index = lastClosedMonthIndex(monthlyBreakdown.length, calculation);
  return index >= 0 ? monthlyBreakdown.slice(0, index + 1) : undefined;
};

// Custo da conta inteira em um unico mes especifico - o ultimo fechado -
// somando todos os servicos em temporalCosts (nao so os que sobraram no
// top-10 de topServices). Funciona pra qualquer provider que preencha
// temporalCosts (AWS/Azure/GCP), sem depender de um campo especifico do
// backend. Distinto de monthlyAverageCost (media) e do total do periodo
// (soma de todos os meses fechados).
export const buildLastClosedMonthAccountCost = (data: any): { value: number; label: string | null } | null => {
  const temporalCosts = data?.temporalCosts || {};
  const months: string[] = Array.isArray(temporalCosts.months) ? temporalCosts.months : [];
  const index = lastClosedMonthIndex(months.length, data?.overview?.monthlyAverageCalculation);
  if (index < 0) return null;

  const value = Object.keys(temporalCosts)
    .filter(key => key !== 'months')
    .reduce((sum, key) => sum + Number((temporalCosts[key] || [])[index] || 0), 0);

  return { value: Number(value.toFixed(2)), label: months[index] || null };
};

// Media e soma de todos os meses FECHADOS, somando todos os servicos em
// temporalCosts (mesma base de buildLastClosedMonthAccountCost, so que pra
// cada mes fechado em vez de so o ultimo). Usado por "Custo Bruto" para
// recalcular Media Mensal/Custo Total sem depender de
// overview.monthlyAverageCost (liquido): numa conta AWS com credito cobrindo
// quase todo o uso, temporalCosts ja vem bruto (ver PR do backend que
// corrigiu Top Services), mas monthlyAverageCost continua liquido - as duas
// fontes divergem de proposito, e esse helper existe pra quem precisar do
// mesmo recorte bruto de temporalCosts nos cards de media/total.
export const buildClosedMonthsAccountCost = (
  data: any
): { average: number; total: number; completedMonths: number } | null => {
  const temporalCosts = data?.temporalCosts || {};
  const months: string[] = Array.isArray(temporalCosts.months) ? temporalCosts.months : [];
  const lastIndex = lastClosedMonthIndex(months.length, data?.overview?.monthlyAverageCalculation);
  if (lastIndex < 0) return null;

  const serviceKeys = Object.keys(temporalCosts).filter(key => key !== 'months');
  const monthlyTotals = Array.from({ length: lastIndex + 1 }, (_, index) =>
    serviceKeys.reduce((sum, key) => sum + Number((temporalCosts[key] || [])[index] || 0), 0)
  );
  const total = monthlyTotals.reduce((sum, value) => sum + value, 0);
  const completedMonths = monthlyTotals.length;

  return {
    average: completedMonths > 0 ? Number((total / completedMonths).toFixed(2)) : 0,
    total: Number(total.toFixed(2)),
    completedMonths
  };
};

export const buildServices = (data: any): ServiceItem[] =>
  (toArray(data?.topServices).length > 0 ? toArray(data?.topServices) : buildServicesFromTemporalCosts(data))
    .map((service: any) => {
      const displayName = formatCloudServiceName(service.name || service.service || 'Serviço');
      const monthlyBreakdown = buildMonthlyBreakdown(data, displayName);
      const closedMonthlyBreakdown = buildClosedMonthlyBreakdown(monthlyBreakdown, data?.overview?.monthlyAverageCalculation);
      // Backend now computes both against the FULL service list (before its
      // own top-10 slice) - percentage is each service's share of everyone's
      // last-closed-month cost, not the period-sum totalCost, so ranking,
      // "% do Total" and this same value always agree (frontend-guardian
      // audit, 2026-09-01). Falls back to a frontend-derived value only for
      // report snapshots taken before these fields existed.
      const lastClosedMonthCost = Number.isFinite(service.lastClosedMonthCost)
        ? Number(service.lastClosedMonthCost)
        : (closedMonthlyBreakdown?.length
          ? Number(closedMonthlyBreakdown[closedMonthlyBreakdown.length - 1].value || 0)
          : Number(service.totalCost || service.cost || 0));

      return {
        name: displayName,
        totalCost: Number(service.totalCost || service.cost || 0),
        percentage: Number(service.percentage || 0),
        // Prefer a backend-computed average when present (e.g. GCP now sends
        // one based on closed months only, consistent with the account-level
        // "média mensal"). Falls back to the old naive division - by every
        // month in temporalCosts, including a still-open current month - for
        // providers/report snapshots that don't send it.
        averageMonthlyCost: Number.isFinite(service.averageMonthlyCost)
          ? Number(service.averageMonthlyCost)
          : Number(service.totalCost || service.cost || 0) / Math.max((data?.temporalCosts?.months || []).length || 1, 1),
        monthlyBreakdown,
        closedMonthlyBreakdown,
        lastClosedMonthCost,
        resources: Array.isArray(service.resources) ? service.resources : []
      };
    })
    // Rank by the same figure that's displayed (lastClosedMonthCost) -
    // sorting by totalCost (period sum) could put a service that spent a
    // lot months ago, but little last month, above one that's actually
    // costing more right now.
    .sort((a, b) => b.lastClosedMonthCost - a.lastClosedMonthCost)
    .slice(0, 10);

export const buildProjects = (data: any): ProjectCostItem[] =>
  toArray(data?.topProjects)
    .map((project: any) => ({
      projectId: String(project.projectId || project.id || ''),
      name: project.name || project.projectName || project.projectId || 'Projeto GCP',
      totalCost: Number(project.totalCost || project.cost || 0),
      // Backend-computed against the full project list (see topServices
      // comment above) - same lastClosedMonthCost basis as the R$ column.
      percentage: Number(project.percentage || 0),
      averageMonthlyCost: Number.isFinite(project.averageMonthlyCost)
        ? Number(project.averageMonthlyCost)
        : Number(project.totalCost || project.cost || 0) / Math.max((data?.temporalCosts?.months || []).length || 1, 1),
      // Backend-computed (gcpRealDataService.ts) - falls back to totalCost
      // for older report snapshots taken before this field existed.
      lastClosedMonthCost: Number.isFinite(project.lastClosedMonthCost)
        ? Number(project.lastClosedMonthCost)
        : Number(project.totalCost || project.cost || 0),
      topService: project.topService ? formatCloudServiceName(project.topService) : undefined
    }))
    .filter(project => project.projectId || project.totalCost > 0)
    .sort((a, b) => b.lastClosedMonthCost - a.lastClosedMonthCost)
    .slice(0, 15);

export const pickTemporalSeries = (data: any, services: ServiceItem[]) => {
  const temporalCosts = data?.temporalCosts || {};
  const months = Array.isArray(temporalCosts.months) ? temporalCosts.months : [];
  const colors = ['#ef4444', '#f59e0b', '#0ea5e9', '#10b981', '#8b5cf6'];

  const datasets = services
    .map((service, index) => {
      const matchKey = findTemporalServiceKey(temporalCosts, service.name);
      const series = matchKey ? temporalCosts[matchKey] : [];
      if (!Array.isArray(series) || !series.length) {
        return null;
      }

      const color = colors[index % colors.length];
      return {
        label: formatCloudServiceName(service.name),
        data: series,
        borderColor: color,
        backgroundColor: `${color}22`,
        fill: true,
        tension: 0.35,
        borderWidth: 3
      };
    })
    .filter(Boolean);

  if (shouldDropPartialCurrentMonth(months, datasets as Array<{ data: number[] }>)) {
    return {
      months: months.slice(0, -1),
      datasets: datasets.map((dataset: any) => ({
        ...dataset,
        data: dataset.data.slice(0, -1)
      }))
    };
  }

  return { months, datasets };
};
